import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { apiErrorMessage } from '../lib/planLimit';
import { FieldInput, PageShell, Panel, ProgressBar, primaryButton, useApi, useTone } from '../components/demarches/ui';

export const ProfilPage = () => {
  const api = useApi();
  const tone = useTone();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/profil').then((d) => { setData(d); setValues(d.profile); }).catch(() => toast.error('Profil indisponible'));
  }, [api]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const changed = Object.fromEntries(Object.entries(values).filter(([k, v]) => v !== data.profile[k]));
      const d = await api.patch('/profil', { values: changed, ...extra });
      setData(d);
      setValues(d.profile);
      toast.success('Profil enregistré : il sera réutilisé dans toutes tes démarches');
    } catch (e) {
      toast.error(apiErrorMessage(e, "Impossible d'enregistrer ton profil"));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <PageShell title="Mon profil"><Loader2 className="animate-spin" /></PageShell>;
  }

  return (
    <PageShell
      title="Mon profil"
      subtitle="Remplis-le une seule fois : Skipya le réutilise pour l'aide au logement, la prime d'activité, tes impôts et le copilote."
      testId="profil-page"
    >
      <Panel>
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="font-semibold">Profil complété à {data.completion.percent} %</span>
          <span className={tone.muted}>{data.completion.missing.length} information(s) essentielle(s) manquante(s)</span>
        </div>
        <ProgressBar value={data.completion.percent} />
      </Panel>

      <Panel title="Réutilisation de tes informations" testId="consent-panel">
        <div className="flex gap-3">
          <ShieldCheck className="text-[#81d8d0] shrink-0" />
          <div className="text-sm leading-6">
            <p className="mb-3">
              Avec ton accord, Skipya reprend automatiquement ton profil et les pièces de ton coffre dans chaque nouvelle démarche.
              Sans accord, il te les propose et tu choisis pièce par pièce. Tu peux changer d'avis à tout moment.
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={primaryButton} disabled={saving || data.reuse_consent === true}
                onClick={() => save({ reuse_consent: true })}>
                {data.reuse_consent === true ? 'Réutilisation activée' : "J'accepte la réutilisation"}
              </button>
              {data.reuse_consent === true && (
                <button type="button" className={`text-sm underline ${tone.muted}`} onClick={() => save({ reuse_consent: false })}>
                  Retirer mon accord
                </button>
              )}
            </div>
          </div>
        </div>
      </Panel>

      <form onSubmit={(e) => { e.preventDefault(); save(); }}>
        {data.sections.map((section) => (
          <Panel key={section.id} title={section.label}>
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(data.fields).filter(([, f]) => f.section === section.id).map(([key, f]) => (
                <FieldInput key={key} field={{ ...f, key }} value={values[key]}
                  onChange={(v) => setValues((prev) => ({ ...prev, [key]: v }))} />
              ))}
            </div>
          </Panel>
        ))}
        <div className="sticky bottom-4 flex justify-end">
          <button type="submit" className={`${primaryButton} shadow-lg`} disabled={saving} data-testid="profil-save">
            {saving && <Loader2 size={16} className="animate-spin" />} Enregistrer mon profil
          </button>
        </div>
      </form>
    </PageShell>
  );
};

export default ProfilPage;
